import classNames from 'classnames';
import {
  ChangeEvent,
  CSSProperties,
  FC,
  FocusEvent,
  HTMLProps,
  KeyboardEvent,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import Icon from 'app/components/v3/components/Icon';
import { EvtKeys } from 'app/types';

import InputBorder from './InputBorder';
import InputField, {
  InputFieldProps,
  inputFontSize,
  inputHeightLarge,
  inputHeightSmall,
  inputLineHeight, InputSize,
} from './InputField';
import Label from './Label';
import SecondaryElementContainer from './SecondaryElementContainer';
import Wrapper from './Wrapper';
import Box from '../Box';
import Legal from '../Text/Legal';
import TooltipIcon, { TooltipIconProps } from '../TooltipIcon';

// Local Typings
interface TextAreaProps {
  isResizable?: boolean;
  maxRows: number;
  minRows?: number;
}
export type InputSizeX = 'full' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs' | 'xxxs';
export interface InputProps extends HTMLProps<HTMLInputElement> {
  disablePaste?: boolean | undefined;
  error?: boolean;
  errorMessage?: null | string;
  forceError?: boolean; // does not require input to have been focused
  fullStoryMask?: boolean;
  helperText?: ReactNode;
  hideLabel?: boolean;
  hideSecondaryElementIfNoValue?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  inputSize?: InputSize;
  /**
   * @deprecated wrap the input in a container with the desired width
   */
  inputSizeX?: InputSizeX;
  isEditable?: boolean;
  label: string;
  maxLength?: number;
  onChange?: (evt: ChangeEvent<HTMLInputElement>) => void;
  onEnter?: (evt: KeyboardEvent) => void;
  prefixElement?: ReactNode;
  rounded?: boolean;
  secondaryElement?: ReactNode;
  secondaryElementAlignSelf?: CSSProperties['alignSelf'];
  select?: boolean;
  textAreaProps?: TextAreaProps;
  tooltipIconProps?: Omit<TooltipIconProps, 'iconProps' | 'v3IconProps'>;
  value: string;
}

// Local Variables
const transition = 100;
const rowHeight = inputFontSize * inputLineHeight;

const getInputHeightFromRows = (rowCount: number) => {
  return rowHeight * rowCount;
};

// Component Definition
const Input: FC<InputFieldProps & InputProps> = ({
  alignment,
  autoComplete = 'off',
  disablePaste,
  error,
  errorMessage,
  forceError,
  fullStoryMask,
  helperText,
  hideLabel,
  hideSecondaryElementIfNoValue,
  inputRef,
  inputSize = 'large',
  isEditable = true,
  label,
  onEnter,
  onKeyDown,
  prefixElement,
  secondaryElement,
  secondaryElementAlignSelf = 'initial',
  textAreaProps,
  tooltipIconProps,
  value,
  ...props
}) => {
  const initialHeight = inputSize === 'large' ? inputHeightLarge : inputHeightSmall;

  const [isFocused, setIsFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [height, setHeight] = useState<'initial' | number>((textAreaProps?.minRows ?? 1) * initialHeight);

  const hasError = !!forceError || (error && touched);

  const localRef = useRef<HTMLInputElement>(null);

  const ref = inputRef ?? localRef;

  const currentRef = ref.current;

  function showHelperText() {
    return hasError ? errorMessage : helperText;
  }

  const helperTextDisplay = showHelperText();

  function handleBlur(evt: FocusEvent<HTMLInputElement>) {
    setTouched(true);
    setIsFocused(false);
    if (props.onBlur) {
      props.onBlur(evt);
    }
  }

  const handleKeyDown = useCallback((evt: KeyboardEvent<HTMLInputElement>) => {
    if (textAreaProps && (evt.key as EvtKeys) === EvtKeys.Backspace) {
      setHeight('initial'); // this handles shrinking the textarea as text is deleted
    }
    if (onEnter && evt.key === 'Enter' && !evt.shiftKey) {
      evt.preventDefault();

      onKeyDown?.(evt);

      onEnter(evt);
    }
  },
  [onEnter, onKeyDown, textAreaProps]);

  useEffect(() => {
    if (textAreaProps) {
      const scrollHeight = ref.current?.scrollHeight ?? 52;

      const rows = scrollHeight / rowHeight;

      const heightFromRows = getInputHeightFromRows(rows);
      const minRowHeight = getInputHeightFromRows(textAreaProps?.minRows ?? 1);
      const maxRowHeight = getInputHeightFromRows(textAreaProps?.maxRows ?? 5);

      const minHeight = Math.max(minRowHeight,
        heightFromRows);

      setHeight(Math.min(maxRowHeight,
        minHeight));
    }
  },
  [ref, textAreaProps]);

  function handleFocus(evt: FocusEvent<HTMLInputElement>) {
    function focus() {
      setIsFocused(true);
      if (props.onFocus) {
        props.onFocus(evt);
      }
    }

    if (props.select) {
      focus();
    } else if (props.readOnly || !isEditable) {
      handleBlur(evt);
    } else {
      focus();
    }
  }
  function handleOnPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    if (disablePaste) {
      e.preventDefault();
    }
  }

  useEffect(() => {
    // [Austin]: 01/05/2021 - not sure why the autofocus attribute is not working
    // should revisit if this component is refactored
    if (props.autoFocus) {
      currentRef?.focus();
    }
  },
  [props.autoFocus, currentRef]);

  const fallbackId = useId();

  const inputId = props.id ?? fallbackId;

  const shouldShowSecondaryElement = !hideSecondaryElementIfNoValue || !!value;
  const resize = textAreaProps?.isResizable ? 'vertical' : 'none';

  const labelComponent = tooltipIconProps
    ? (
      <Label
        alignItems="center"
        display="flex"
        gap={1}
        htmlFor={inputId}
      >
        <span>
          {label}
        </span>

        <TooltipIcon
          {...tooltipIconProps}
          v3IconProps={{
            name: 'alert-info',
            size: 'xxxs',
          }}
        />
      </Label>
    )
    : (
      <Label htmlFor={inputId}>
        {label}
      </Label>
    );

  return (
    <Wrapper
      className="input_input-wrapper"
      inputSizeX={props.inputSizeX}
    >
      {!hideLabel && labelComponent}

      <Box
        alignItems="center"
        display="flex"
        fullStoryMask={fullStoryMask}
      >
        <InputBorder
          className="input_input-border"
          error={hasError}
          isDisabled={!!props.disabled}
          isEditable={isEditable}
          isFocused={isFocused}
          rounded={!!props.rounded}
          transition={transition}
        >
          <Box
            alignItems="center"
            display="flex"
            flexGrow={1}
            flexWrap="nowrap"
          >
            {prefixElement}

            <InputField
              autoComplete={autoComplete}
              data-testid="input"
              onPaste={handleOnPaste}
              {...props}
              alignment={alignment}
              aria-label={label}
              as={textAreaProps ? 'textarea' : 'input'}
              className={classNames(props.className, 'input_input-field')}
              id={inputId}
              inputHeight={height}
              inputSize={inputSize}
              isTextArea={!!textAreaProps}
              onBlur={handleBlur}
              onChange={props.onChange}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              placeholder={props.placeholder}
              readOnly={!!props.readOnly || !isEditable}
              ref={ref}
              resize={textAreaProps ? resize : undefined}
              rows={textAreaProps?.minRows ?? 1}
              value={value}
            />
            {secondaryElement && shouldShowSecondaryElement && (
              <SecondaryElementContainer
                alignSelf={secondaryElementAlignSelf}
                className="input_secondary-element-container"
              >
                {secondaryElement}
              </SecondaryElementContainer>
            )}
          </Box>
        </InputBorder>
      </Box>

      {helperTextDisplay && (
        <Box
          alignItems="center"
          display="flex"
          gap={2}
          marginTop={1}
        >
          {hasError && (
            <Icon
              color="danger"
              name="alert-error"
            />
          )}

          <Legal>
            {helperTextDisplay}
          </Legal>
        </Box>
      )}
    </Wrapper>
  );
};

export default Input;
